import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { sellerService } from "@/services/sellerService";
import { walletService } from "@/services/walletService";

// Get date ranges for comparison
const getDateRanges = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const sixtyDaysAgo = new Date(now);
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

  return {
    current: {
      start: thirtyDaysAgo.toISOString(),
      end: now.toISOString(),
    },
    previous: {
      start: sixtyDaysAgo.toISOString(),
      end: thirtyDaysAgo.toISOString(),
    },
  };
};

interface DashboardStats {
  currentBookings: number;
  currentRevenue: number;
  bookingChange: number;
  revenueChange: number;
  transactionsCount: number;
}

interface UseDashboardDataOptions {
  /** Host dashboard renders recent txs; partner dashboard does not. */
  includeRecentTransactions?: boolean;
}

/**
 * Custom hook for fetching and computing dashboard data for a wallet owner
 * (seller or host). KPIs use GET /users/{id}/wallet/stats (ledger aggregates),
 * same as Wallet tab.
 */
export function useDashboardData(
  walletOwnerUserId: string | undefined,
  options: UseDashboardDataOptions = {},
) {
  const { includeRecentTransactions = false } = options;
  const dateRanges = useMemo(() => getDateRanges(), []);

  const {
    data: walletData,
    isLoading: isLoadingWallet,
    error: walletError,
  } = useQuery({
    queryKey: ["sellerWallet", walletOwnerUserId],
    queryFn: () => sellerService.getWallet(walletOwnerUserId!),
    enabled: !!walletOwnerUserId,
  });

  const {
    data: currentPeriodStats,
    isLoading: isLoadingCurrentStats,
    error: currentStatsError,
  } = useQuery({
    queryKey: [
      "sellerWalletStats",
      walletOwnerUserId,
      "dashboard",
      "current",
      dateRanges.current,
    ],
    queryFn: () =>
      walletService.getStats(walletOwnerUserId!, {
        start_date: dateRanges.current.start,
        end_date: dateRanges.current.end,
      }),
    enabled: !!walletOwnerUserId,
  });

  const {
    data: previousPeriodStats,
    isLoading: isLoadingPreviousStats,
    error: previousStatsError,
  } = useQuery({
    queryKey: [
      "sellerWalletStats",
      walletOwnerUserId,
      "dashboard",
      "previous",
      dateRanges.previous,
    ],
    queryFn: () =>
      walletService.getStats(walletOwnerUserId!, {
        start_date: dateRanges.previous.start,
        end_date: dateRanges.previous.end,
      }),
    enabled: !!walletOwnerUserId,
  });

  const { data: recentTx } = useQuery({
    queryKey: ["sellerWalletTransactions", walletOwnerUserId, "recent"],
    queryFn: () =>
      walletService.getTransactions(walletOwnerUserId!, { limit: 8 }),
    enabled: !!walletOwnerUserId && includeRecentTransactions,
  });

  const stats: DashboardStats = useMemo(() => {
    const currentRevenue = currentPeriodStats?.total_credits ?? 0;
    const previousRevenue = previousPeriodStats?.total_credits ?? 0;
    const currentBookings = currentPeriodStats?.credit_transactions_count ?? 0;
    const previousBookings =
      previousPeriodStats?.credit_transactions_count ?? 0;

    const bookingChange =
      previousBookings > 0
        ? Math.round(
            ((currentBookings - previousBookings) / previousBookings) * 100,
          )
        : currentBookings > 0
          ? 100
          : 0;

    const revenueChange =
      previousRevenue > 0
        ? Math.round(
            ((currentRevenue - previousRevenue) / previousRevenue) * 100,
          )
        : currentRevenue > 0
          ? 100
          : 0;

    return {
      currentBookings,
      currentRevenue,
      bookingChange,
      revenueChange,
      transactionsCount: currentPeriodStats?.transactions_count ?? 0,
    };
  }, [currentPeriodStats, previousPeriodStats]);

  const isLoading =
    isLoadingWallet || isLoadingCurrentStats || isLoadingPreviousStats;

  const combinedError = walletError ?? currentStatsError ?? previousStatsError;

  return {
    walletBalance: walletData?.wallet_balance || 0,
    stats,
    recentTransactions: recentTx?.items || [],
    isLoading,
    error: combinedError,
  };
}
